import { createClient } from "@/app/utils/supabase/client";
import {
  SignedUrlCache,
  SignedUrlCacheInsert,
  SignedUrlCacheUpdate,
} from "@/app/lib/supabase/types";
import { SUPABASE_TABLES } from "../supabase/supabaseDBConstants";

export const signedUrlCacheApi = {
  /**
   * Get a signed URL from cache by bucket and file path
   */
  async getSignedUrl(
    companyId: string,
    bucket: string,
    filePath: string
  ): Promise<SignedUrlCache | null> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SIGNED_URL_CACHE)
      .select("*")
      .eq("company_id", companyId)
      .eq("bucket_name", bucket)
      .eq("file_storage_path", filePath)
      .gt("expires_at", new Date().toISOString())
      .single();

    if (error && error.code !== "PGRST116") {
      // PGRST116 is "no rows returned"
      console.error("Error fetching signed URL from cache:", error);
      throw error;
    }

    return data || null;
  },

  /**
   * Get all signed URLs for a folder
   */
  async getSignedUrlsForFolder(
    companyId: string,
    bucket: string,
    folderPath: string
  ): Promise<SignedUrlCache[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SIGNED_URL_CACHE)
      .select("*")
      .eq("company_id", companyId)
      .eq("bucket_name", bucket)
      .eq("folder_storage_path", folderPath)
      .gt("expires_at", new Date().toISOString())
      .order("file_storage_path");

    if (error) {
      console.error("Error fetching signed URLs for folder:", error);
      throw error;
    }

    return data || [];
  },

  /**
   * Get multiple signed URLs by paths (batch operation)
   */
  async getSignedUrlsBatch(
    companyId: string,
    requests: Array<{ bucket: string; filePath: string }>
  ): Promise<SignedUrlCache[]> {
    const supabase = createClient();

    // Due to PostgREST limitations with complex OR queries,
    // we'll fetch all non-expired URLs for the company and filter in memory
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SIGNED_URL_CACHE)
      .select("*")
      .eq("company_id", companyId)
      .gt("expires_at", new Date().toISOString());

    if (error) {
      console.error("Error fetching signed URLs batch:", error);
      throw error;
    }

    if (!data) return [];

    // Filter the results to match our requests
    const requestMap = new Map(
      requests.map(req => [`${req.bucket}:${req.filePath}`, req])
    );

    return data.filter(item => 
      requestMap.has(`${item.bucket_name}:${item.file_storage_path}`)
    );
  },

  /**
   * Add a signed URL to cache
   */
  async addSignedUrl(signedUrl: SignedUrlCacheInsert): Promise<SignedUrlCache> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SIGNED_URL_CACHE)
      .upsert(signedUrl, {
        onConflict: "bucket_name,file_storage_path",
      })
      .select()
      .single();

    if (error) {
      console.error("Error adding signed URL to cache:", error);
      throw error;
    }

    return data;
  },

  /**
   * Add multiple signed URLs to cache (batch operation)
   */
  async addSignedUrlsBatch(
    signedUrls: SignedUrlCacheInsert[]
  ): Promise<SignedUrlCache[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SIGNED_URL_CACHE)
      .upsert(signedUrls, {
        onConflict: "bucket_name,file_storage_path",
      })
      .select();

    if (error) {
      console.error("Error adding signed URLs batch to cache:", error);
      throw error;
    }

    return data || [];
  },

  /**
   * Update a signed URL in cache
   */
  async updateSignedUrl(
    id: string,
    updates: SignedUrlCacheUpdate
  ): Promise<SignedUrlCache> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.SIGNED_URL_CACHE)
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Error updating signed URL in cache:", error);
      throw error;
    }

    return data;
  },

  /**
   * Delete expired signed URLs
   */
  async deleteExpiredUrls(companyId?: string): Promise<void> {
    const supabase = createClient();

    let query = supabase
      .from(SUPABASE_TABLES.SIGNED_URL_CACHE)
      .delete()
      .lt("expires_at", new Date().toISOString());

    if (companyId) {
      query = query.eq("company_id", companyId);
    }

    const { error } = await query;

    if (error) {
      console.error("Error deleting expired signed URLs:", error);
      throw error;
    }
  },

  /**
   * Delete specific signed URLs by file paths
   */
  async deleteSignedUrls(
    companyId: string,
    bucket: string,
    filePaths: string[]
  ): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.SIGNED_URL_CACHE)
      .delete()
      .eq("company_id", companyId)
      .eq("bucket_name", bucket)
      .in("file_storage_path", filePaths);

    if (error) {
      console.error("Error deleting signed URLs:", error);
      throw error;
    }
  },

  /**
   * Call the cleanup function in the database
   */
  async runCleanupFunction(): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase.rpc("cleanup_expired_signed_urls");

    if (error) {
      console.error("Error running cleanup function:", error);
      throw error;
    }
  },
};